import { useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { useAuth } from '@/context/AuthContext';
import { authAPI } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { User, Mail, Phone, Shield, Calendar } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminProfilePage() {
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      await authAPI.updateProfile({ name, phone });
      toast.success('Profile updated');
      refreshUser();
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
    finally { setLoading(false); }
  };

  return (
    <AdminLayout>
      <div className="max-w-2xl space-y-6" data-testid="admin-profile-page">
        <div>
          <h1 className="text-xl font-bold font-['Outfit'] text-[var(--text-primary)]">Admin Profile</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">Manage your administrator account</p>
        </div>

        <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg p-6 space-y-5">
          <div className="flex items-center gap-4 pb-5 border-b border-[var(--border-subtle)]">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
              <span className="text-2xl font-bold text-green-400">{user?.name?.[0]?.toUpperCase()}</span>
            </div>
            <div>
              <p className="text-lg font-semibold text-[var(--text-primary)]">{user?.name}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="px-2 py-0.5 rounded text-xs bg-green-500/15 text-green-400 font-medium">Administrator</span>
                <span className={`px-2 py-0.5 rounded text-xs ${user?.kyc_status === 'verified' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'}`}>KYC: {user?.kyc_status}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs text-[var(--text-secondary)] flex items-center gap-1.5"><User className="w-3 h-3" /> Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="admin-profile-name" className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] h-11" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-[var(--text-secondary)] flex items-center gap-1.5"><Mail className="w-3 h-3" /> Email</Label>
              <Input value={user?.email || ''} disabled className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-secondary)] h-11 cursor-not-allowed" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-[var(--text-secondary)] flex items-center gap-1.5"><Phone className="w-3 h-3" /> Phone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="admin-profile-phone" className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] h-11" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-[var(--text-secondary)] flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Joined</Label>
              <Input value={user?.created_at ? new Date(user.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' }) : ''} disabled className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-secondary)] h-11 cursor-not-allowed" />
            </div>
          </div>

          <button onClick={handleSave} disabled={loading} data-testid="admin-profile-save" className="px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-all disabled:opacity-40">
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg p-6 space-y-3">
          <h3 className="text-sm font-semibold font-['Outfit'] text-[var(--text-primary)] flex items-center gap-2"><Shield className="w-4 h-4 text-[var(--text-secondary)]" /> Security Info</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="p-3 bg-[var(--bg-primary)] rounded-lg border border-[var(--border-subtle)]">
              <span className="text-xs text-[var(--text-secondary)]">Role</span>
              <p className="font-medium text-green-400 mt-1">Administrator</p>
            </div>
            <div className="p-3 bg-[var(--bg-primary)] rounded-lg border border-[var(--border-subtle)]">
              <span className="text-xs text-[var(--text-secondary)]">User ID</span>
              <p className="font-mono text-xs text-[var(--text-secondary)] mt-1 truncate">{user?.id}</p>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
